/**
 * Shared helpers for the travel planner.
 */

import type { TravelEventType, TravelTransitMode } from '../api';

/**
 * Defense-in-depth URL sanitizer applied at render time. The server-side
 * `sanitizeAIUrl` (services/api-service/src/services/safe-url.ts) is the
 * primary guarantee — it drops non-HTTP(S) URLs at persist time so they
 * never reach the DB. This client copy guards historical rows persisted
 * before the server check shipped, and any other render path that bypasses
 * the persist sanitizer. Returns `null` for anything not http(s):.
 */
export function sanitizeSourceUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

// Default event type colors
const DEFAULT_EVENT_TYPE_COLORS: Record<TravelEventType, string> = {
  sight: '#7c3aed', // purple
  food: '#dc4a5a', // soft red
  transport: '#d4a020', // yellow
  hotel: '#22a55a', // green
  activity: '#3b82f6', // blue
  note: '#94a3b8', // muted slate
};

// Mutable user overrides — persisted in localStorage
const STORAGE_KEY = 'fias-travel-event-colors';

function loadUserColors(): Partial<Record<TravelEventType, string>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const userColorOverrides: Partial<Record<TravelEventType, string>> = loadUserColors();

export function getEventTypeColor(type: TravelEventType): string {
  return userColorOverrides[type] ?? DEFAULT_EVENT_TYPE_COLORS[type];
}

export function setEventTypeColor(type: TravelEventType, color: string): void {
  userColorOverrides[type] = color;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userColorOverrides));
  } catch {
    // localStorage unavailable — in-memory only
  }
}

/** @deprecated Use getEventTypeColor() instead. Kept for any remaining static reads. */
export const EVENT_TYPE_COLORS: Record<TravelEventType, string> = new Proxy(
  DEFAULT_EVENT_TYPE_COLORS,
  { get: (_target, prop: string) => getEventTypeColor(prop as TravelEventType) },
);

export const EVENT_TYPE_LABELS: Record<TravelEventType, string> = {
  sight: 'Sightseeing',
  food: 'Food & Drink',
  transport: 'Transport',
  hotel: 'Accommodation',
  activity: 'Activity',
  note: 'Note',
};

// City color palette — purple-to-light-blue gradient (auto-assigned, cycling)
const CITY_COLORS = [
  '#7c3aed',
  '#6d5ce7',
  '#5b7ff5',
  '#38bdf8',
  '#8b5cf6',
  '#a78bfa',
  '#60a5fa',
  '#22d3ee',
  '#818cf8',
  '#7dd3fc',
];

const CITY_COLORS_STORAGE_KEY = 'fias-travel-city-colors';

function loadCityColors(): Record<string, string> {
  try {
    const raw = localStorage.getItem(CITY_COLORS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const cityColorMap = new Map<string, string>();
const userCityColors: Record<string, string> = loadCityColors();
let colorIndex = 0;

export function getCityColor(city: string): string {
  if (!city) return '#94a3b8';
  const key = city.toLowerCase();
  // User override takes priority
  if (userCityColors[key]) return userCityColors[key];
  if (!cityColorMap.has(key)) {
    cityColorMap.set(key, CITY_COLORS[colorIndex % CITY_COLORS.length]);
    colorIndex++;
  }
  return cityColorMap.get(key)!;
}

export function setCityColor(city: string, color: string): void {
  const key = city.toLowerCase();
  userCityColors[key] = color;
  cityColorMap.set(key, color);
  try {
    localStorage.setItem(CITY_COLORS_STORAGE_KEY, JSON.stringify(userCityColors));
  } catch {
    // localStorage unavailable
  }
}

export function resetCityColors(): void {
  cityColorMap.clear();
  colorIndex = 0;
  // Re-apply user overrides after reset
  for (const [key, color] of Object.entries(userCityColors)) {
    cityColorMap.set(key, color);
  }
}

// Day-index color palette — used by both the timeline legend and the map view
// to give each day a distinct marker color. Cycles for trips longer than the
// palette length.
const DAY_COLORS = [
  '#7c3aed',
  '#dc4a5a',
  '#22a55a',
  '#d4a020',
  '#3b82f6',
  '#8b5cf6',
  '#f97316',
  '#14b8a6',
  '#ec4899',
  '#0ea5e9',
];

/** Returns the marker color for a 1-based day number. */
export function getDayColor(dayNumber: number | null | undefined): string {
  if (!dayNumber || dayNumber < 1) return '#94a3b8';
  return DAY_COLORS[(dayNumber - 1) % DAY_COLORS.length];
}

// Transit mode emoji mapping
export const TRANSIT_MODE_EMOJI: Record<TravelTransitMode, string> = {
  Flight: '\u2708\ufe0f',
  Train: '\ud83d\ude84',
  Bus: '\ud83d\ude8c',
  Drive: '\ud83d\ude97',
  Ferry: '\u26f4\ufe0f',
  Helicopter: '\ud83d\ude81',
  Other: '\ud83d\udce6',
};

// Format date from trip start + day offset
export function formatDayDate(startDate: string, dayNumber: number): string {
  const date = new Date(startDate + 'T00:00:00');
  date.setDate(date.getDate() + (dayNumber - 1));
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${weekday} ${month}/${day}`;
}

export function getDayDate(startDate: string, dayNumber: number): Date {
  const date = new Date(startDate + 'T00:00:00');
  date.setDate(date.getDate() + (dayNumber - 1));
  return date;
}

export function formatDateRange(startDate: string, totalDays: number): string {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + totalDays - 1);
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${startStr} \u2013 ${endStr}`;
}

/**
 * Format a cents amount as a compact currency string.
 * Returns an em-dash for null/undefined so empty cells render cleanly.
 */
export function formatTodoCost(cents: number | null | undefined, currency: string = 'USD'): string {
  if (cents === null || cents === undefined) return '\u2014';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
